import { Link } from 'react-router-dom';

interface Recommendation {
  type: 'product' | 'blog';
  title: string;
  description: string;
  href: string;
  icon: string;
}

interface BlogRecommendationsProps {
  topic: 'term-life' | 'whole-life' | 'final-expense' | 'annuities' | 'mortgage-protection' | 'iul' | 'wealth-building' | 'special-cases' | 'universal-life';
}

const recommendationMap: Record<string, Recommendation[]> = {
  'term-life': [
    {
      type: 'product',
      title: 'Term Life Insurance',
      description: 'Affordable coverage for a specific period. Perfect for young families and mortgage protection.',
      href: '/term-life',
      icon: 'ri-shield-check-line'
    },
    {
      type: 'product',
      title: 'Mortgage Protection Insurance',
      description: 'Specialized term coverage designed to pay off your mortgage if something happens to you.',
      href: '/mortgage-protection',
      icon: 'ri-home-heart-line'
    },
    {
      type: 'blog',
      title: 'Term vs. Whole Life Insurance',
      description: 'Compare the two most popular types of life insurance and find which one fits your needs.',
      href: '/blog/term-vs-whole-life-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'whole-life': [
    {
      type: 'product',
      title: 'Whole Life Insurance',
      description: 'Permanent coverage with guaranteed cash value growth. Build wealth while protecting your family.',
      href: '/whole-life',
      icon: 'ri-infinity-line'
    },
    {
      type: 'product',
      title: 'Wealth Building Strategies',
      description: 'Use life insurance as a tax-advantaged wealth building tool for your family\'s future.',
      href: '/wealth-building',
      icon: 'ri-line-chart-line'
    },
    {
      type: 'blog',
      title: 'Wealth Building with Life Insurance',
      description: 'Discover how permanent life insurance can become a powerful wealth accumulation vehicle.',
      href: '/blog/wealth-building-life-insurance-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'final-expense': [
    {
      type: 'product',
      title: 'Final Expense Insurance',
      description: 'Affordable coverage for seniors to cover funeral costs and final expenses. No medical exam required.',
      href: '/final-expense',
      icon: 'ri-hand-heart-line'
    },
    {
      type: 'product',
      title: 'No Medical Exam Life Insurance',
      description: 'Get approved quickly without needles or doctor visits. Ideal for seniors and those with health concerns.',
      href: '/no-medical-exam',
      icon: 'ri-heart-pulse-line'
    },
    {
      type: 'blog',
      title: 'Life Insurance for Seniors Over 60',
      description: 'Options and strategies for Indianapolis seniors looking for affordable coverage.',
      href: '/blog/life-insurance-seniors-over-60-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'annuities': [
    {
      type: 'product',
      title: 'Annuities',
      description: 'Guaranteed retirement income you can\'t outlive. Protect your retirement from market volatility.',
      href: '/annuities',
      icon: 'ri-calendar-check-line'
    },
    {
      type: 'product',
      title: 'Generational Wealth Planning',
      description: 'Build a legacy that lasts. Strategies to pass wealth to your children and grandchildren tax-efficiently.',
      href: '/generational-wealth',
      icon: 'ri-parent-line'
    },
    {
      type: 'blog',
      title: 'Annuities for Retirement Planning',
      description: 'How annuities can provide guaranteed income and peace of mind in retirement.',
      href: '/blog/annuities-retirement-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'mortgage-protection': [
    {
      type: 'product',
      title: 'Mortgage Protection Insurance',
      description: 'Ensure your family keeps the home if something happens to you. Declining benefit matches your mortgage.',
      href: '/mortgage-protection',
      icon: 'ri-home-heart-line'
    },
    {
      type: 'product',
      title: 'Term Life Insurance',
      description: 'Flexible term coverage that can protect your mortgage and provide additional family protection.',
      href: '/term-life',
      icon: 'ri-shield-check-line'
    },
    {
      type: 'blog',
      title: 'Mortgage Protection: A Fishers Family Story',
      description: 'Real story of how mortgage protection insurance saved a local family\'s home.',
      href: '/resources/mortgage-protection-fishers-family-story',
      icon: 'ri-article-line'
    }
  ],
  'iul': [
    {
      type: 'product',
      title: 'Indexed Universal Life Insurance',
      description: 'Permanent coverage with cash value tied to market indexes. Growth potential with downside protection.',
      href: '/indexed-universal-life',
      icon: 'ri-stock-line'
    },
    {
      type: 'product',
      title: 'Universal Life Insurance',
      description: 'Flexible permanent coverage with adjustable premiums and death benefits.',
      href: '/universal-life',
      icon: 'ri-settings-3-line'
    },
    {
      type: 'blog',
      title: 'Indexed Universal Life Insurance Guide',
      description: 'Everything you need to know about IUL policies and how they work in Indianapolis.',
      href: '/blog/indexed-universal-life-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'wealth-building': [
    {
      type: 'product',
      title: 'Wealth Building Strategies',
      description: 'Tax-advantaged strategies using life insurance to build and transfer wealth.',
      href: '/wealth-building',
      icon: 'ri-line-chart-line'
    },
    {
      type: 'product',
      title: 'Indexed Universal Life Insurance',
      description: 'Build cash value with market-linked growth potential and tax-free access to funds.',
      href: '/indexed-universal-life',
      icon: 'ri-stock-line'
    },
    {
      type: 'blog',
      title: 'Wealth Building with Life Insurance',
      description: 'How high-net-worth families use permanent life insurance as a wealth accumulation tool.',
      href: '/blog/wealth-building-life-insurance-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'special-cases': [
    {
      type: 'product',
      title: 'No Medical Exam Life Insurance',
      description: 'Get approved without needles or doctor visits. Perfect for those with health concerns.',
      href: '/no-medical-exam',
      icon: 'ri-heart-pulse-line'
    },
    {
      type: 'product',
      title: 'Term Life Insurance',
      description: 'Affordable coverage options with flexible underwriting for various health conditions.',
      href: '/term-life',
      icon: 'ri-shield-check-line'
    },
    {
      type: 'blog',
      title: 'Life Insurance with Diabetes',
      description: 'How to get approved for life insurance even with diabetes or pre-diabetes.',
      href: '/blog/life-insurance-diabetes-indianapolis',
      icon: 'ri-article-line'
    }
  ],
  'universal-life': [
    {
      type: 'product',
      title: 'Universal Life Insurance',
      description: 'Flexible permanent coverage with adjustable premiums and death benefits to fit your changing needs.',
      href: '/universal-life',
      icon: 'ri-settings-3-line'
    },
    {
      type: 'product',
      title: 'Indexed Universal Life Insurance',
      description: 'Universal life with market-linked growth potential and downside protection.',
      href: '/indexed-universal-life',
      icon: 'ri-stock-line'
    },
    {
      type: 'blog',
      title: 'Universal Life Insurance Explained',
      description: 'Complete guide to universal life insurance and how it compares to other permanent policies.',
      href: '/blog/universal-life-insurance-indianapolis',
      icon: 'ri-article-line'
    }
  ]
};

export default function BlogRecommendations({ topic }: BlogRecommendationsProps) {
  const recommendations = recommendationMap[topic] || recommendationMap['term-life'];

  return (
    <section className="py-16 bg-gradient-to-br from-orange-50 via-white to-teal-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
            <i className="ri-bookmark-line text-xl text-white"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">You May Also Like</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {recommendations.map((rec, index) => (
            <Link
              key={index}
              to={rec.href}
              className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-200 hover:-translate-y-1"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${rec.icon} text-2xl text-white`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      rec.type === 'product' 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-teal-100 text-teal-700'
                    }`}>
                      {rec.type === 'product' ? 'Product' : 'Article'}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                    {rec.title}
                  </h3>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                {rec.description}
              </p>

              <div className="flex items-center gap-2 text-orange-600 font-semibold text-sm group-hover:gap-3 transition-all">
                <span>Learn More</span>
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}